/**
 * MessengerDrawer — 149-cw (PROMPT 16-CHAT-UX-NOTIFICATION-R2).
 *
 * Right-side NON-MODAL drawer (420px desktop, full-width mobile). Tidak
 * mem-block scroll halaman utama & tidak menampilkan overlay abu-abu.
 * Header baru: judul "Pesan Internal" + titik hijau "Terhubung" saat koneksi
 * aktif (menyembunyikan istilah teknis "Polling" dari user).
 * Tabs: Semua / Belum Dibaca / Kontak. Tab Kontak menampilkan SEMUA user
 * aktif (bukan hanya yang online): online paling atas, lalu offline diurutkan
 * dari terakhir-online terbaru, lalu "Belum pernah online" di paling bawah.
 * Siapa yang tampil bisa diatur per-user lewat ikon ⚙ (contact-filter).
 * untuk unread. Time format ID-locale: 19.12 · Kemarin · 23 Agu.
 * Realtime tetap via `ens:msg` bus + polling incremental fallback (invisible).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Search, Send, ArrowLeft, BellOff, Volume2, VolumeX, Loader2, Edit3, Trash2, Plus, Settings } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useMessagingPrefs from "@/hooks/useMessagingPrefs";
import { fmtRelativeLastSeen, fmtLastSeenExact } from "@/lib/presence";
import MessengerContactFilterDialog from "./MessengerContactFilterDialog";

const POLL_MS = 8000;

function useAudioBeep() {
  const ctxRef = useRef(null);
  const enable = () => {
    try {
      if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current.resume?.();
    } catch { /* browser block */ }
  };
  const beep = () => {
    if (!ctxRef.current) return false;
    try {
      const ctx = ctxRef.current;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 660;
      g.gain.value = 0.06;
      o.connect(g); g.connect(ctx.destination);
      o.start(); setTimeout(() => { o.stop(); }, 120);
      return true;
    } catch { return false; }
  };
  return { enable, beep };
}

export default function MessengerDrawer({ onClose, onUnreadChange, realtimeStatus, setRealtimeStatus, initialConversationId, onSelectedConvChange }) {
  const { user } = useAuth();
  const { prefs, update: updatePrefs } = useMessagingPrefs(true);
  const [convs, setConvs] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [newQ, setNewQ] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [presence, setPresence] = useState({});
  const [loading, setLoading] = useState(true);
  // 149-cw-contact-filter: tab "Kontak" — daftar semua user + presence + filter setting.
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactFilter, setContactFilter] = useState({ mode: "all", divisions: [], user_ids: [] });
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [busySend, setBusySend] = useState(false);
  const [composer, setComposer] = useState("");
  const [editing, setEditing] = useState(null);
  const lastPollRef = useRef(new Date().toISOString());
  const scrollRef = useRef(null);
  const audio = useAudioBeep();

  useEffect(() => { onSelectedConvChange?.(selected?.id || null); }, [selected, onSelectedConvChange]);

  const savePrefs = async (patch) => {
    try { await updatePrefs(patch); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal simpan preferensi"); }
  };

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/messaging/conversations");
      setConvs(data || []);
      onUnreadChange?.((data || []).reduce((s, c) => s + (c.unread_count || 0), 0));
      const ids = (data || []).map((c) => c.other_user.id);
      if (ids.length) {
        try {
          const { data: pres } = await api.get(`/messaging/presence?ids=${ids.join(",")}`);
          const map = {}; pres.forEach((p) => { map[p.user_id] = p; });
          setPresence(map);
        } catch { /* silent */ }
      }
      return data || [];
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal muat percakapan"); return []; }
    finally { setLoading(false); }
  }, [onUnreadChange]);

  const loadUsers = useCallback(async () => {
    try { const { data } = await api.get("/users"); setUsers((data || []).filter((u) => u.id !== user.id)); } catch { /* silent */ }
  }, [user.id]);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const { data } = await api.get("/messaging/contacts");
      setContacts(data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal muat kontak"); }
    finally { setContactsLoading(false); }
  }, []);

  const loadContactFilter = useCallback(async () => {
    try {
      const { data } = await api.get("/messaging/contact-filter");
      setContactFilter(data || { mode: "all", divisions: [], user_ids: [] });
    } catch { /* silent */ }
  }, []);

  const loadMessages = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/messaging/conversations/${cid}/messages?limit=50`);
      setMessages(data || []);
      api.post(`/messaging/conversations/${cid}/read`, {}).then(loadConvs).catch(() => {});
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, [loadConvs]);

  useEffect(() => {
    (async () => {
      const list = await loadConvs();
      await loadUsers();
      loadContacts();
      loadContactFilter();
      if (initialConversationId) {
        const target = (list || []).find((c) => c.id === initialConversationId);
        if (target) setSelected(target);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]);

  // 149-cw-contact: refresh daftar kontak saat tab Kontak aktif (polling 15 dtk).
  useEffect(() => {
    if (tab !== "kontak") return;
    loadContacts();
    const t = setInterval(loadContacts, 15000);
    return () => clearInterval(t);
  }, [tab, loadContacts]);

  useEffect(() => { if (selected) loadMessages(selected.id); }, [selected, loadMessages]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, selected]);

  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/messaging/incremental?since=${encodeURIComponent(lastPollRef.current)}`);
        lastPollRef.current = data.as_of;
        if ((data.messages || []).length > 0) {
          setRealtimeStatus?.((s) => s === "realtime" ? s : "polling");
          if (selected) {
            const rel = data.messages.filter((m) => m.conversation_id === selected.id);
            if (rel.length) {
              setMessages((prev) => {
                const ex = new Set(prev.map((m) => m.id));
                return [...prev, ...rel.filter((m) => !ex.has(m.id))];
              });
              api.post(`/messaging/conversations/${selected.id}/read`, {}).catch(() => {});
            }
          }
          const anyOther = data.messages.some((m) => m.sender_id !== user.id);
          const anyOtherFromClosedConv = data.messages.some(
            (m) => m.sender_id !== user.id && (!selected || m.conversation_id !== selected.id)
          );
          if (anyOther) {
            loadConvs();
            if (anyOtherFromClosedConv && prefs.sound && !prefs.dnd) audio.beep();
          }
        }
      } catch { setRealtimeStatus?.("reconnecting"); }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [selected, loadConvs, prefs, setRealtimeStatus, user.id, audio]);

  useEffect(() => {
    const on = (e) => {
      const p = e.detail || {};
      if (p.type === "message_new") {
        if (selected && p.conversation_id === selected.id) {
          setMessages((prev) => (prev.find((m) => m.id === p.message?.id) ? prev : [...prev, p.message]));
          api.post(`/messaging/conversations/${selected.id}/read`, {}).catch(() => {});
        } else {
          loadConvs();
          if (prefs.sound && !prefs.dnd && p.message?.sender_id !== user.id) audio.beep();
        }
      } else if (p.type === "message_read" && selected && p.conversation_id === selected.id) {
        setMessages((prev) => prev.map((m) => m.sender_id === user.id && !m.read_at && m.sent_at <= p.up_to ? { ...m, read_at: p.up_to } : m));
      } else if (p.type === "message_edit" && selected && p.conversation_id === selected.id) {
        setMessages((prev) => prev.map((m) => m.id === p.message_id ? { ...m, body: p.body, edited_at: p.edited_at } : m));
      }
    };
    window.addEventListener("ens:msg", on);
    return () => window.removeEventListener("ens:msg", on);
  }, [selected, loadConvs, prefs, user.id, audio]);

  const openWithUser = async (u) => {
    audio.enable();
    setShowNewMenu(false); setNewQ("");
    try {
      const { data } = await api.post("/messaging/conversations", { other_user_id: u.id });
      setSelected({ id: data.id, other_user: u });
      loadConvs();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!composer.trim() || !selected) return;
    audio.enable();
    setBusySend(true);
    try {
      if (editing) {
        await api.patch(`/messaging/messages/${editing.id}`, { body: composer });
        setEditing(null);
      } else {
        await api.post(`/messaging/conversations/${selected.id}/messages`, { body: composer });
      }
      setComposer("");
      await loadMessages(selected.id);
      loadConvs();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal kirim"); }
    finally { setBusySend(false); }
  };

  const hideMessage = async (m) => {
    if (!window.confirm("Sembunyikan pesan ini dari tampilan Anda?")) return;
    try { await api.delete(`/messaging/messages/${m.id}`); await loadMessages(selected.id); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const filteredConvs = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (convs || []).filter((c) => {
      const uname = (c.other_user?.name || "").toLowerCase();
      const preview = (c.last_preview || "").toLowerCase();
      const div = (c.other_user?.division || "").toLowerCase();
      const matchQ = !qq || uname.includes(qq) || preview.includes(qq) || div.includes(qq);
      if (!matchQ) return false;
      if (tab === "unread") return (c.unread_count || 0) > 0;
      return true;
    });
  }, [convs, q, tab, presence]);

  const filteredContacts = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (contacts || []).filter((c) => {
      if (!qq) return true;
      return (c.name || "").toLowerCase().includes(qq)
        || (c.display_name || "").toLowerCase().includes(qq)
        || (c.division || "").toLowerCase().includes(qq)
        || (c.email || "").toLowerCase().includes(qq);
    });
  }, [contacts, q]);

  const totalUnread = (convs || []).reduce((s, c) => s + (c.unread_count || 0), 0);
  const totalOnline = (contacts || []).filter((c) => c.is_online).length;

  const newQ_users = useMemo(() => {
    if (!newQ.trim()) return users.slice(0, 20);
    const qq = newQ.toLowerCase();
    return users.filter((u) => (u.name || "").toLowerCase().includes(qq) || (u.email || "").toLowerCase().includes(qq) || (u.division || "").toLowerCase().includes(qq)).slice(0, 20);
  }, [newQ, users]);

  const isConnected = realtimeStatus !== "reconnecting";

  return (
    <aside
      data-testid="messenger-drawer"
      role="complementary"
      aria-label="Pesan Internal"
      className="fixed top-0 right-0 z-40 h-full w-full sm:w-[420px] bg-white shadow-[-8px_0_20px_-4px_rgba(11,29,58,0.15)] flex flex-col border-l border-slate-200"
    >
        <header className="bg-[#0B1D3A] text-white px-4 py-3 flex items-center gap-2" data-testid="messenger-header">
          {selected ? (
            <>
              <button onClick={() => setSelected(null)} aria-label="Kembali ke daftar percakapan" data-testid="msg-back" title="Kembali" className="text-white/70 hover:text-white p-1.5"><ArrowLeft size={18} /></button>
              <Avatar name={selected.other_user.name} online={!!presence[selected.other_user.id]?.is_online} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{selected.other_user.display_name || selected.other_user.name?.split(" ")?.[0]}</div>
                <div className="text-[10px] text-white/70 truncate">
                  {selected.other_user.division} Lv{selected.other_user.level} · <span data-testid="msg-presence-label" title={presence[selected.other_user.id]?.last_active_at ? `Terakhir aktif: ${new Date(presence[selected.other_user.id].last_active_at).toLocaleString("id-ID")}` : undefined}>
                    {presence[selected.other_user.id]?.is_online ? <span className="text-emerald-300">Online</span> : `Terakhir online ${fmtLastSeenExact(presence[selected.other_user.id]?.last_active_at)}`}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">Pesan Internal</div>
              <div className="text-[10px] text-white/70 flex items-center gap-1.5" data-testid="messenger-connection-indicator">
                {isConnected ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" aria-hidden="true" />
                    <span>Terhubung</span>
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" aria-hidden="true" />
                    <span>Menyambung ulang…</span>
                  </>
                )}
              </div>
            </div>
          )}
          <button onClick={() => savePrefs({ sound: !prefs.sound })} aria-label={prefs.sound ? "Matikan suara notifikasi" : "Nyalakan suara notifikasi"} data-testid="msg-toggle-sound" title={prefs.sound ? "Suara aktif — klik untuk mute" : "Suara mati — klik untuk aktifkan"} className="text-white/70 hover:text-white p-2">
            {prefs.sound ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button onClick={() => savePrefs({ dnd: !prefs.dnd })} aria-label={prefs.dnd ? "Matikan Jangan Ganggu" : "Aktifkan Jangan Ganggu"} data-testid="msg-toggle-dnd" title={prefs.dnd ? "Jangan Ganggu aktif — klik untuk nonaktifkan" : "Aktifkan Jangan Ganggu (mute semua notifikasi)"} className={`p-2 ${prefs.dnd ? "text-amber-300" : "text-white/70 hover:text-white"}`}>
            <BellOff size={16} />
          </button>
          <button onClick={onClose} aria-label="Tutup panel Pesan Internal" data-testid="msg-close" title="Tutup" className="text-white/70 hover:text-white p-2"><X size={18} /></button>
        </header>

        {!selected ? (
          <>
            <div className="border-b border-slate-200 flex" data-testid="msg-tabs" role="tablist" aria-label="Filter percakapan">
              {[
                { k: "all",    label: "Semua",         badge: null },
                { k: "unread", label: "Belum Dibaca",  badge: totalUnread },
                { k: "kontak", label: "Kontak",        badge: totalOnline || null },
              ].map((t) => (
                <button key={t.k} type="button" onClick={() => setTab(t.k)} role="tab" aria-selected={tab === t.k} data-testid={`msg-tab-${t.k}`}
                  className={`flex-1 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider transition-colors ${tab === t.k ? "text-[#0B1D3A] border-b-2 border-[#FF5A1F]" : "text-slate-500 hover:text-slate-700 border-b-2 border-transparent"}`}>
                  {t.label}
                  {t.badge > 0 && (
                    <span data-testid={`msg-tab-badge-${t.k}`} className={`ml-1.5 inline-block min-w-[18px] px-1 py-0.5 text-[9px] rounded-full tabular-nums ${tab === t.k ? "bg-[#FF5A1F] text-white" : "bg-slate-200 text-slate-700"}`}>
                      {t.badge > 99 ? "99+" : t.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-2.5 border-b border-slate-200 flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="msg-search" aria-label="Cari percakapan" placeholder="Cari nama atau pesan…"
                  className="w-full pl-8 pr-2 py-2 border border-slate-300 text-xs focus:outline-none focus:border-[#FF5A1F] rounded-none" />
              </div>
              {tab === "kontak" && (
                <button type="button" onClick={() => setShowFilterDialog(true)} aria-label="Atur filter kontak" title="Pengaturan kontak (siapa saja yang tampil)" data-testid="msg-contact-filter-btn"
                  className="w-9 h-9 flex items-center justify-center rounded-none border border-slate-300 text-slate-600 hover:border-[#FF5A1F] hover:text-[#FF5A1F] shrink-0">
                  <Settings size={15} />
                </button>
              )}
              <button type="button" onClick={() => { setShowNewMenu((v) => !v); setNewQ(""); }} aria-label="Mulai percakapan baru" title="Percakapan baru" data-testid="msg-new-conv-btn"
                className={`w-9 h-9 flex items-center justify-center rounded-none ${showNewMenu ? "bg-[#e04a13]" : "bg-[#FF5A1F] hover:bg-[#e04a13]"} text-white shrink-0`}>
                <Plus size={16} />
              </button>
            </div>

            {showNewMenu && (
              <div className="p-2.5 border-b border-slate-200 bg-orange-50/40" data-testid="msg-new-conv-picker">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] mb-1.5">Pilih staff</div>
                <input value={newQ} onChange={(e) => setNewQ(e.target.value)} data-testid="msg-new-conv-search" aria-label="Cari staff" placeholder="Cari nama / email / divisi…" autoFocus
                  className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:outline-none focus:border-[#FF5A1F] rounded-none mb-2" />
                <ul className="max-h-56 overflow-y-auto border border-slate-200 bg-white" data-testid="msg-new-conv-results">
                  {newQ_users.length === 0
                    ? <li className="px-2 py-2 text-[11px] italic text-slate-400">Tidak ada hasil</li>
                    : newQ_users.map((u) => (
                    <li key={u.id} data-testid={`msg-new-conv-user-${u.id}`} onClick={() => openWithUser({ ...u, display_name: (u.nickname || u.name?.split(" ")[0] || u.name) })}
                      className="px-2 py-1.5 hover:bg-slate-50 cursor-pointer flex items-center gap-2 border-b border-slate-100 last:border-b-0">
                      <Avatar name={u.name} sm />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#0B1D3A] truncate">{u.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{u.division} Lv{u.level} · {u.email}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex-1 overflow-y-auto" data-testid="msg-conv-list">
              {tab === "kontak" ? (
                contactsLoading && contacts.length === 0 ? (
                  <div className="p-8 text-center text-xs text-slate-400 italic">Memuat kontak…</div>
                ) : filteredContacts.length === 0 ? (
                  <div className="p-8 text-center text-xs text-slate-500 italic">
                    {q.trim() ? "Tidak ada kontak cocok dengan pencarian." : "Tidak ada kontak tampil. Atur lewat ikon ⚙ di atas."}
                  </div>
                ) : (
                  <ul>
                    {[
                      { key: "online", label: "Online", items: filteredContacts.filter((c) => c.is_online) },
                      { key: "offline", label: "Offline", items: filteredContacts.filter((c) => !c.is_online && c.last_active_at) },
                      { key: "never", label: "Belum pernah online", items: filteredContacts.filter((c) => !c.is_online && !c.last_active_at) },
                    ].filter((g) => g.items.length > 0).map((g) => (
                      <li key={g.key} data-testid={`msg-contact-group-${g.key}`}>
                        <div className="px-3 py-1.5 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-y border-slate-200">{g.label} · {g.items.length}</div>
                        <ul className="divide-y divide-slate-100">
                          {g.items.map((c) => (
                            <li key={c.id} data-testid={`msg-contact-${c.id}`} onClick={() => openWithUser(c)}
                              className="px-3 py-2.5 cursor-pointer flex items-center gap-2.5 hover:bg-slate-50 transition-colors"
                              title={`Chat dengan ${c.name}`}>
                              <Avatar name={c.name} online={c.is_online} />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-800 truncate">{c.display_name || c.name?.split(" ")[0]}</div>
                                <div className="text-[10px] text-slate-500 truncate">
                                  {c.division} · Lv{c.level}
                                  {c.is_online
                                    ? <span className="ml-1.5 text-emerald-600 font-semibold" data-testid={`msg-contact-online-${c.id}`}>· Online</span>
                                    : c.last_active_at
                                      ? <span className="ml-1.5 text-slate-400" data-testid={`msg-contact-lastseen-${c.id}`} title={`Terakhir aktif: ${new Date(c.last_active_at).toLocaleString("id-ID")}`}>· Online {fmtLastSeenExact(c.last_active_at)}</span>
                                      : <span className="ml-1.5 text-slate-400 italic" data-testid={`msg-contact-never-${c.id}`}>· Belum pernah online</span>}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                )
              ) : loading ? (
                <div className="p-8 text-center text-xs text-slate-400 italic">Memuat…</div>
              ) : filteredConvs.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-500 italic">
                  {q.trim() ? "Tidak ada percakapan cocok."
                    : tab === "unread" ? "Semua sudah dibaca 👌"
                    : "Belum ada percakapan. Klik + untuk mulai."}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {[...filteredConvs].sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || "")).map((c) => {
                    const isUnread = (c.unread_count || 0) > 0;
                    const isOnline = !!presence[c.other_user?.id]?.is_online;
                    return (
                      <li key={c.id} data-testid={`msg-conv-${c.id}`} onClick={() => setSelected(c)}
                        className={`px-3 py-2.5 cursor-pointer flex items-center gap-2.5 transition-colors ${isUnread ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"}`}
                        data-unread={isUnread ? "1" : "0"}>
                        <Avatar name={c.other_user.name} online={isOnline} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`text-sm truncate ${isUnread ? "font-bold text-[#0B1D3A]" : "font-semibold text-slate-800"}`}>
                              {c.other_user.display_name || c.other_user.name?.split(" ")[0]}
                            </span>
                            <span className={`text-[10px] tabular-nums whitespace-nowrap ${isUnread ? "text-blue-700 font-semibold" : "text-slate-400"}`}>
                              {tsSmart(c.last_message_at)}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {c.other_user.division} · Lv{c.other_user.level}
                            {!isOnline && presence[c.other_user?.id]?.last_active_at && (
                              <span className="ml-1.5 text-slate-400" data-testid={`msg-conv-lastseen-${c.id}`}>· Online {fmtLastSeenExact(presence[c.other_user.id].last_active_at)}</span>
                            )}
                            {isOnline && <span className="ml-1.5 text-emerald-600 font-semibold">· Online</span>}
                          </div>
                          <div className={`text-xs truncate mt-0.5 ${isUnread ? "font-semibold text-slate-800" : "text-slate-600"}`}>
                            {c.last_preview || <span className="italic text-slate-400">Belum ada pesan</span>}
                          </div>
                        </div>
                        {isUnread && (
                          <span data-testid={`msg-conv-unread-${c.id}`} className="min-w-[20px] h-5 px-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center tabular-nums shrink-0" title={`${c.unread_count} pesan belum dibaca`}>
                            {c.unread_count > 99 ? "99+" : c.unread_count}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 bg-slate-50 space-y-2" data-testid="msg-thread">
              {messages.length === 0 && <div className="text-center text-xs text-slate-400 italic py-6">Belum ada pesan. Kirim yang pertama.</div>}
              {messages.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} data-testid={`msg-item-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[80%]">
                      <div className={`px-3 py-1.5 text-xs whitespace-pre-wrap ${mine ? "bg-[#0B1D3A] text-white rounded-l-lg rounded-tr-lg" : "bg-white border border-slate-200 text-slate-800 rounded-r-lg rounded-tl-lg"}`}>
                        {m.body}
                      </div>
                      <div className={`flex items-center gap-1 mt-0.5 text-[9px] ${mine ? "justify-end text-slate-400" : "justify-start text-slate-500"}`}>
                        <span>{tsSmart(m.sent_at)}</span>
                        {m.edited_at && <span title={`Diedit ${tsSmart(m.edited_at)}`}>(diedit)</span>}
                        {mine && (m.read_at ? <span className="text-emerald-600" title="Dibaca">✓✓</span> : <span title="Terkirim">✓</span>)}
                        {mine && (
                          <>
                            <button onClick={() => { setEditing(m); setComposer(m.body); }} aria-label="Edit pesan" title="Edit" className="hover:text-[#FF5A1F] ml-1" data-testid={`msg-edit-${m.id}`}><Edit3 size={9} /></button>
                            <button onClick={() => hideMessage(m)} aria-label="Sembunyikan pesan" title="Sembunyikan" className="hover:text-rose-600" data-testid={`msg-hide-${m.id}`}><Trash2 size={9} /></button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-2 border-t border-slate-200 bg-white flex items-end gap-2 relative">
              {editing && (
                <div className="absolute -top-6 left-2 text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 px-2 py-0.5">
                  Mengedit · <button onClick={() => { setEditing(null); setComposer(""); }} className="underline">batal</button>
                </div>
              )}
              <textarea value={composer} onChange={(e) => setComposer(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                data-testid="msg-composer" rows={1} aria-label="Tulis pesan (Enter untuk kirim, Shift+Enter baris baru)" placeholder="Tulis pesan…"
                className="flex-1 border border-slate-300 px-2 py-2 text-xs resize-none max-h-32 focus:outline-none focus:border-[#FF5A1F] rounded-none" />
              <button onClick={send} disabled={busySend || !composer.trim()} data-testid="msg-send" aria-label="Kirim pesan" title="Kirim (Enter)"
                className="h-9 px-3 bg-[#FF5A1F] hover:bg-[#e04a13] text-white uppercase text-xs tracking-wider font-semibold disabled:opacity-40 flex items-center gap-1">
                {busySend ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Kirim
              </button>
            </div>
          </>
        )}
        <MessengerContactFilterDialog
          open={showFilterDialog}
          onClose={() => setShowFilterDialog(false)}
          allUsers={users}
          currentFilter={contactFilter}
          onSaved={(f) => { setContactFilter(f); loadContacts(); }}
        />
    </aside>
  );
}

function Avatar({ name, sm, online }) {
  const initials = ((name || "?").split(" ").slice(0, 2).map((s) => s[0]).join("")).toUpperCase();
  return (
    <div className="relative shrink-0">
      <div className={`${sm ? "w-6 h-6 text-[9px]" : "w-9 h-9 text-[11px]"} rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#e04a13] text-white font-bold flex items-center justify-center`} aria-hidden="true">
        {initials}
      </div>
      {online != null && (
        <span aria-label={online ? "Online" : "Offline"}
          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? "bg-emerald-500" : "bg-slate-400"}`} />
      )}
    </div>
  );
}

function tsSmart(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }).replace(":", ".");
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Kemarin";
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
  } catch { return ""; }
}
